// ReportForm.js — tema claro + layout=wide + valida Content-Type
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const TOC_REPORT_VARIABLE_ORDER = [
  "TOC",
  "CONDUCTIVIDAD_COMPENSADA",
  "CONDUCTIVITY_COMP",
  "TEMP",
  "TEMP_1",
  "TEMP_2",
  "TEMPERATURA",
  "TEMPERATURE",
];

function normalizeReportVariable(variable) {
  return String(variable || "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();
}

function sortReportVariables(variables) {
  const values = Array.isArray(variables) ? [...variables] : [];

  const hasToc = values.some(
    (variable) => normalizeReportVariable(variable) === "TOC"
  );

  if (!hasToc) return values;

  const priority = new Map(
    TOC_REPORT_VARIABLE_ORDER.map((variable, index) => [
      normalizeReportVariable(variable),
      index,
    ])
  );

  return values
    .map((variable, originalIndex) => ({ variable, originalIndex }))
    .sort((left, right) => {
      const leftIndex =
        priority.get(normalizeReportVariable(left.variable)) ??
        Number.MAX_SAFE_INTEGER;
      const rightIndex =
        priority.get(normalizeReportVariable(right.variable)) ??
        Number.MAX_SAFE_INTEGER;

      if (leftIndex !== rightIndex) return leftIndex - rightIndex;
      return left.originalIndex - right.originalIndex;
    })
    .map(({ variable }) => variable);
}

export default function ReportForm() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const API = (p) => `${backend}${p}`;
  const token = localStorage.getItem("token") || "";

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sensorType, setSensorType] = useState("");
  const [devices, setDevices] = useState([]);
  const [sensorId, setSensorId] = useState("");
  const [vars, setVars] = useState([]);
  const [selectedVars, setSelectedVars] = useState([]);
  const [sampling, setSampling] = useState("all");

  const [usersList, setUsersList] = useState([]);
  const [viewAs, setViewAs] = useState("");
  const [officialModalOpen, setOfficialModalOpen] = useState(false);
  const [officialReason, setOfficialReason] = useState("");
  const [officialSignatureMeaning, setOfficialSignatureMeaning] = useState("Aprobación");
  const [officialSignaturePassword, setOfficialSignaturePassword] = useState("");
  const [part11Enabled, setPart11Enabled] = useState(false);
  const [officialLoading, setOfficialLoading] = useState(false);

  const authHdr = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const withAs = (url) => (viewAs ? `${url}${url.includes("?") ? "&" : "?"}as=${encodeURIComponent(viewAs)}` : url);

  // La frecuencia de 20 minutos es exclusiva para MOLINOS CHAMPION.
  // También se habilita cuando un administrador usa "Ver como: molinos".
  const isMolinosReportUser = useMemo(() => {
    const effectiveUsername = String(
      viewAs || user?.username || user?.name || ""
    )
      .trim()
      .toLowerCase();

    const effectiveTeam = String(
      user?.team_name || user?.teamName || user?.team || ""
    )
      .trim()
      .toUpperCase();

    return (
      effectiveUsername === "molinos" ||
      effectiveUsername === "molinos champion" ||
      effectiveTeam === "MOLINOS CHAMPION"
    );
  }, [
    viewAs,
    user?.username,
    user?.name,
    user?.team_name,
    user?.teamName,
    user?.team,
  ]);

  // Si un admin estaba en MOLINOS con 20 minutos seleccionado y cambia
  // a otro cliente, regresamos automáticamente a "Todos los datos".
  useEffect(() => {
    if (!isMolinosReportUser && sampling === "twenty_minutes") {
      setSampling("all");
    }
  }, [isMolinosReportUser, sampling]);

  useEffect(() => {
    const parts = window.location.pathname.split("/");
    const maybeType = decodeURIComponent(parts[parts.length - 1] || "");
    if (maybeType) setSensorType(maybeType);
    const q = new URLSearchParams(window.location.search);
    const sid = q.get("sensorId"); if (sid) setSensorId(sid);
    const as = q.get("as"); if (as) setViewAs(as);
  }, []);

  useEffect(() => {
    fetch(API("/api/sensores/admin/users"), authHdr)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(arr => Array.isArray(arr) ? setUsersList(arr) : setUsersList([]))
      .catch(() => setUsersList([]));
    // eslint-disable-next-line
  }, []);

useEffect(() => {
  fetch(API("/api/sensores/admin/electronic-signatures-summary"), authHdr)
    .then(r => (r.ok ? r.json() : Promise.reject()))
    .then(data => setPart11Enabled(!!data?.team?.part11_enabled))
    .catch(() => setPart11Enabled(false));
  // eslint-disable-next-line
}, []);

  useEffect(() => {
    setDevices([]);
    if (!sensorType) return;
    fetch(withAs(API(`/api/sensores/devices?type=${encodeURIComponent(sensorType)}`)), authHdr)
      .then(r => (r.ok ? r.json() : []))
      .then(list => setDevices(Array.isArray(list) ? list : []))
      .catch(() => setDevices([]));
    // eslint-disable-next-line
  }, [sensorType, viewAs]);

  useEffect(() => {
    setVars([]); setSelectedVars([]);
    if (!sensorId) return;
    fetch(withAs(API(`/api/sensores/variables/${encodeURIComponent(sensorId)}`)), authHdr)
      .then(r => (r.ok ? r.json() : []))
.then(arr => {
  const excludedVars = ["BAT", "BATV", "BATTERY", "BATTERY_LEVEL"];

  const filteredVariables = Array.isArray(arr)
    ? arr.filter(variable => {
        const normalized = String(variable || "")
          .trim()
          .toUpperCase();

        return !excludedVars.includes(normalized);
      })
    : [];

  const orderedVariables = sortReportVariables(filteredVariables);

  setVars(orderedVariables);
  setSelectedVars(orderedVariables.slice(0, 3));
})
      .catch(() => setVars([]));
    // eslint-disable-next-line
  }, [sensorId, viewAs]);

  const invalidRange = Boolean(startDate && endDate && startDate > endDate);
  const valid = Boolean(startDate && endDate && sensorType && !invalidRange);

  const safeDateForFilename = (value) =>
    String(value || "").replace("T", "_").replace(/:/g, "-");

  const displayDateTime = (value) =>
    value ? String(value).replace("T", " ") : "—";
  const toggleVar = (variable) =>
    setSelectedVars((current) =>
      sortReportVariables(
        current.includes(variable)
          ? current.filter((item) => item !== variable)
          : [...current, variable]
      )
    );

async function download(fmt) {
    try {
      if (!startDate || !endDate || !sensorType) {
        alert("Completa fecha, hora y tipo de sensor.");
        return;
      }
      if (invalidRange) {
        alert("La fecha y hora inicial no puede ser posterior a la final.");
        return;
      }
      if (sampling === "twenty_minutes" && !isMolinosReportUser) {
        alert("La frecuencia de 20 minutos solo está habilitada para MOLINOS CHAMPION.");
        return;
      }
      const qs = new URLSearchParams();
      qs.set("start_date", startDate);
      qs.set("end_date", endDate);
      qs.set("type", sensorType);
      qs.set("format", fmt === "excel" ? "excel" : "pdf");
      qs.set("layout", "wide");
      qs.set("sampling", sampling);
      if (sensorId) qs.set("sensorId", sensorId);
      if (selectedVars?.length) {
        qs.set(
          "variables",
          sortReportVariables(selectedVars).join(",")
        );
      }
      if (viewAs) qs.set("as", viewAs);

      const url = API(`/api/report?${qs.toString()}`);
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

      const ct = (resp.headers.get("content-type") || "").toLowerCase();
      const expect = fmt === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";

      if (!resp.ok || !ct.includes(expect)) {
      const rawMsg = await resp.text();
      let cleanMsg = "No fue posible generar el archivo.";

      try {
      const parsed = JSON.parse(rawMsg);
      cleanMsg = parsed.error || parsed.message || cleanMsg;
      } catch (e) {
      cleanMsg = rawMsg || cleanMsg;
      }

      alert(cleanMsg);
      return;
      }

      const blob = await resp.blob();
      const ext = fmt === "excel" ? "xlsx" : "pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const safeStart = safeDateForFilename(startDate);
      const safeEnd = safeDateForFilename(endDate);
      const samplingSuffix =
        sampling === "alarmed_only"
          ? "_datos_alarmados"
          : sampling === "three_daily"
          ? "_3_datos_por_dia"
          : sampling === "twenty_minutes"
          ? "_promedio_20_min"
          : "";
      a.download = `reporte_${sensorType}_${safeStart}_${safeEnd}${samplingSuffix}${ext === "xlsx" ? "_wide" : ""}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    } catch (e) {
      console.error(e);
      alert("No fue posible generar el archivo.");
    }
  }

  function openOfficialModal() {
    if (!startDate || !endDate || !sensorType) {
      alert("Completa fecha, hora y tipo de sensor.");
      return;
    }
    if (invalidRange) {
      alert("La fecha y hora inicial no puede ser posterior a la final.");
      return;
    }
    setOfficialReason("");
    setOfficialSignatureMeaning("Aprobación");
    setOfficialSignaturePassword("");
    setOfficialModalOpen(true);
  }

  function closeOfficialModal() {
    setOfficialModalOpen(false);
    setOfficialReason("");
    setOfficialSignatureMeaning("Aprobación");
    setOfficialSignaturePassword("");
    setOfficialLoading(false);
  }

  async function downloadOfficialPdf() {
    try {
      if (!startDate || !endDate || !sensorType) {
        alert("Completa fecha, hora y tipo de sensor.");
        return;
      }
      if (invalidRange) {
        alert("La fecha y hora inicial no puede ser posterior a la final.");
        return;
      }
      if (sampling === "twenty_minutes" && !isMolinosReportUser) {
        alert("La frecuencia de 20 minutos solo está habilitada para MOLINOS CHAMPION.");
        return;
      }

      const reason = String(officialReason || "").trim();
      const signatureMeaning = String(officialSignatureMeaning || "").trim();
      const signaturePassword = String(officialSignaturePassword || "").trim();

      if (!reason || reason.length < 5) {
        alert("Debes ingresar un motivo válido.");
        return;
      }

      if (!signatureMeaning || signatureMeaning.length < 3) {
        alert("Debes seleccionar el significado de la firma.");
        return;
      }

      if (!signaturePassword) {
        alert("Debes ingresar tu contraseña.");
        return;
      }

      setOfficialLoading(true);

      const qs = new URLSearchParams();
      qs.set("start_date", startDate);
      qs.set("end_date", endDate);
      qs.set("type", sensorType);
      qs.set("format", "pdf");
      qs.set("layout", "wide");
      qs.set("sampling", sampling);
      qs.set("official", "true");
      qs.set("reason", reason);
      qs.set("signature_meaning", signatureMeaning);
      qs.set("signature_password", signaturePassword);
      if (sensorId) qs.set("sensorId", sensorId);
      if (selectedVars?.length) {
        qs.set(
          "variables",
          sortReportVariables(selectedVars).join(",")
        );
      }
      if (viewAs) qs.set("as", viewAs);

      const url = API(`/api/report?${qs.toString()}`);
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const ct = (resp.headers.get("content-type") || "").toLowerCase();
       if (!resp.ok || !ct.includes("application/pdf")) {
      const rawMsg = await resp.text();
      let cleanMsg = "No fue posible generar el PDF CFR21.";

      try {
      const parsed = JSON.parse(rawMsg);
      cleanMsg = parsed.error || parsed.message || cleanMsg;
      } catch (e) {
      cleanMsg = rawMsg || cleanMsg;
      }

      alert(cleanMsg);
      return;
      }

      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const safeStart = safeDateForFilename(startDate);
      const safeEnd = safeDateForFilename(endDate);
      const samplingSuffix =
        sampling === "alarmed_only"
          ? "_datos_alarmados"
          : sampling === "three_daily"
          ? "_3_datos_por_dia"
          : sampling === "twenty_minutes"
          ? "_promedio_20_min"
          : "";
      a.download = `reporte_CFR21_${sensorType}_${safeStart}_${safeEnd}${samplingSuffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);

      closeOfficialModal();
    } catch (e) {
      console.error(e);
      alert("No fue posible generar el PDF CFR21.");
    } finally {
      setOfficialLoading(false);
    }
  }
  return (
    <div style={sx.page}>
      {/* Header con botón Volver */}
      <div style={{ ...sx.header, display: "flex", alignItems: "center", gap: 12 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          title="Volver"
          style={sx.btnBack}
        >
          ← Volver
        </button>
        <div>
          <h2 style={sx.title}>Generar reporte</h2>
          <p style={sx.subtitle}>Elige rango, sensor y variables a incluir en el archivo.</p>
        </div>
      </div>

      <div style={sx.container}>
        {/* Rango */}
        <div style={sx.card}>
          <div style={sx.cardHeader}>Rango de fecha y hora</div>
          <div style={sx.grid2}>
            <label style={sx.label}>
              <span style={sx.labelText}>Desde</span>
              <input
                type="datetime-local"
                step="60"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={sx.input}
              />
            </label>
            <label style={sx.label}>
              <span style={sx.labelText}>Hasta</span>
              <input
                type="datetime-local"
                step="60"
                min={startDate || undefined}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={sx.input}
              />
            </label>
          </div>
          {invalidRange && (
            <div style={sx.errorText}>
              La fecha y hora inicial no puede ser posterior a la final.
            </div>
          )}
        </div>

        {/* Sensor */}
        <div style={sx.card}>
          <div style={sx.cardHeader}>Sensor</div>
          <div style={sx.grid2}>
            <label style={sx.label}>
              <span style={sx.labelText}>Tipo de sensor</span>
              <input type="text" value={sensorType} onChange={e=>setSensorType(e.target.value)} style={sx.input}/>
            </label>
            <label style={sx.label}>
              <span style={sx.labelText}>Sensor (opcional)</span>
              <select value={sensorId} onChange={e=>setSensorId(e.target.value)} style={sx.input}>
                <option value="">— Todos —</option>
                {devices.map(d => {
                  const value = d.device_id || d.id || d.deveui || d.devEUI;
                  const label = d.name || d.device_id || d.id || d.deveui || d.devEUI;
                  return <option key={value} value={value}>{label}</option>;
                })}
              </select>
            </label>
          </div>

          {/* Ver como (ADMIN) */}
          {usersList.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <label style={sx.label}>
                <span style={sx.labelText}>Ver como</span>
                <select value={viewAs} onChange={e=>setViewAs(e.target.value)} style={sx.input}>
                  <option value="">(ADMIN)</option>
                  {usersList.map(u => <option key={u.id} value={u.username}>{u.username}</option>)}
                </select>
              </label>
            </div>
          )}
        </div>

        {/* Variables */}
        <div style={sx.card}>
          <div style={sx.cardHeader}>Variables</div>
          {sensorId ? (
            <div style={sx.chipsWrap}>
              {vars.map(v => {
                const checked = selectedVars.includes(v);
                return (
                  <label key={v} style={{ ...sx.chip, ...(checked ? sx.chipActive : {}) }} title={checked ? "Incluida" : "Marcar para incluir"}>
                    <input type="checkbox" checked={checked} onChange={e=>setSelectedVars(prev=> e.target.checked ? Array.from(new Set([...prev, v])) : prev.filter(x=>x!==v))} style={{ display:"none" }}/>
                    {v}
                  </label>
                );
              })}
              {!vars.length && <div style={{ opacity: .7 }}>No se detectaron variables para este sensor.</div>}
            </div>
          ) : (
            <div style={{ opacity: .7 }}>Selecciona un sensor para ver variables disponibles.</div>
          )}
        </div>

              {/* Frecuencia del reporte */}
        <div style={sx.card}>
          <div style={sx.cardHeader}>Frecuencia del reporte</div>

          <label style={sx.label}>
            <span style={sx.labelText}>Cantidad de datos a incluir</span>

            <select
              value={sampling}
              onChange={(e) => setSampling(e.target.value)}
              style={sx.input}
            >
              <option value="all">Todos los datos disponibles</option>
              <option value="three_daily">3 datos por día</option>

              {isMolinosReportUser && (
                <option value="twenty_minutes">
                  Promedio cada 20 minutos
                </option>
              )}

              <option value="alarmed_only">Solo datos alarmados</option>
            </select>
          </label>

          {sampling === "three_daily" && (
            <div style={sx.samplingInfo}>
              Se seleccionará la lectura más cercana a las 08:00, 13:00 y
              20:00 de cada día, con una tolerancia máxima de 2 horas.
            </div>
          )}

          {sampling === "twenty_minutes" && (
            <div style={sx.samplingInfo}>
              Se calculará el promedio de las lecturas de cada bloque de
              20 minutos: 00–19, 20–39 y 40–59. Los datos originales
              continuarán almacenados cada minuto.
            </div>
          )}

          {sampling === "alarmed_only" && (
            <div style={sx.alarmSamplingInfo}>
              Se incluirán únicamente las lecturas que estén fuera de los
              límites mínimo o máximo configurados en Alertas / Umbrales.
            </div>
          )}

          {sampling !== "alarmed_only" && (
            <div style={sx.alarmLegend}>
              Los valores fuera de los umbrales configurados aparecerán
              resaltados en rojo en el PDF y en el Excel.
            </div>
          )}
        </div>
      


        {/* Acciones */}
        <div style={{ ...sx.card, ...sx.actionsCard }}>
          <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>  
            <button style={sx.btnPrimary} onClick={() => download("pdf")} disabled={!valid}>
             Descargar PDF
             </button>

             {part11Enabled && (
             <button style={sx.btnOfficial} onClick={openOfficialModal} disabled={!valid}>
             Descargar PDF CFR21
             </button>
             )}

            <button style={sx.btnSuccess} onClick={() => download("excel")} disabled={!valid}>
            Descargar Excel
            </button>
            <div style={{ flex:1 }} />
            <div style={sx.metaRight}>
              <span style={sx.badge}>{selectedVars.length || 0} vars</span>
              <span style={{ opacity:.7 }}>
                {displayDateTime(startDate)} → {displayDateTime(endDate)}
              </span>
            </div>
          </div>
        </div>
      </div>



      {officialModalOpen && (
        <div style={sx.modalOverlay} onClick={closeOfficialModal}>
          <div style={sx.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={sx.modalHeader}>
              <div>
                <h3 style={sx.modalTitle}>Descargar PDF CFR21</h3>
                <p style={sx.modalSubtitle}>
                  Este reporte se generará como evidencia firmada bajo CFR 21 Part 11.
                </p>
              </div>
            </div>

            <div style={sx.modalBody}>
              <label style={sx.label}>
                <span style={sx.labelText}>Motivo</span>
                <textarea
                  value={officialReason}
                  onChange={(e) => setOfficialReason(e.target.value)}
                  style={sx.textarea}
                  placeholder="Describe el motivo de la generación del reporte CFR21..."
                />
              </label>

              <label style={sx.label}>
                <span style={sx.labelText}>Significado de firma</span>
                <select
                  value={officialSignatureMeaning}
                  onChange={(e) => setOfficialSignatureMeaning(e.target.value)}
                  style={sx.input}
                >
                  <option value="Aprobación">Aprobación</option>
                  <option value="Revisión">Revisión</option>
                  <option value="Autorización">Autorización</option>
                </select>
              </label>

              <label style={sx.label}>
                <span style={sx.labelText}>Contraseña</span>
                <input
                  type="password"
                  value={officialSignaturePassword}
                  onChange={(e) => setOfficialSignaturePassword(e.target.value)}
                  style={sx.input}
                  placeholder="Ingresa tu contraseña actual"
                  autoComplete="new-password"
                />
              </label>
            </div>

            <div style={sx.modalActions}>
              <button type="button" style={sx.btnGhost} onClick={closeOfficialModal}>
                Cancelar
              </button>

              <button
                type="button"
                style={sx.btnOfficial}
                onClick={downloadOfficialPdf}
                disabled={officialLoading}
              >
                {officialLoading ? "Generando..." : "Confirmar y descargar"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* ===== Tema claro ===== */
const ink   = "#0f172a";
const panel = "#ffffff";
const panelSoft = "#f8fafc";
const brd   = "#e2e8f0";
const acc   = "#2563eb";
const acc2  = "#10b981";

const sx = {
  page: { maxWidth: 1100, margin: "24px auto", padding: "0 16px", color: ink },
  header: { marginBottom: 12 },
  title: { margin: 0, fontWeight: 900, letterSpacing: 0.2 },
  subtitle: { margin: "6px 0 0", opacity: 0.8 },
  container: { display: "grid", gap: 14 },
  card: { borderRadius: 16, padding: 16, background: panel, border: `1px solid ${brd}`, boxShadow: "0 10px 24px rgba(2,6,23,.10)" },
  cardHeader: { fontWeight: 800, marginBottom: 10, opacity: 0.95 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: { display: "flex", flexDirection: "column", gap: 6 },
  labelText: { fontSize: 13, opacity: 0.85 },
  input: { height: 44, padding: "10px 12px", borderRadius: 12, border: `1px solid ${brd}`, background: panelSoft, color: ink, outline: "none" },
  errorText: { marginTop: 10, color: "#b91c1c", fontSize: 13, fontWeight: 700 },
  chipsWrap: { display: "flex", gap: 10, flexWrap: "wrap" },
  chip: { padding: "8px 12px", borderRadius: 999, border: `1px solid ${brd}`, cursor: "pointer", userSelect: "none", transition: "all .15s ease", background: "rgba(2,6,23,.05)", color: ink },
  chipActive: { border: `1px solid ${acc}`, background: "rgba(37,99,235,.18)" },
  actionsCard: { position: "sticky", bottom: 12, backdropFilter: "blur(6px)" },
  btnPrimary: { background: acc, color: "#fff", borderRadius: 10, border: 0, padding: "10px 16px", cursor:"pointer" },
  btnSuccess: { background: acc2, color: "#fff", borderRadius: 10, border: 0, padding: "10px 16px", cursor:"pointer" },
  metaRight: { display: "flex", alignItems: "center", gap: 10 },
  badge: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 36, height: 28, padding: "0 10px", borderRadius: 999, background: "rgba(2,6,23,.10)", border: `1px solid ${brd}`, fontSize: 12 },
  samplingInfo: {
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 10,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1e40af",
  fontSize: 13,
  lineHeight: 1.5,
},
  alarmSamplingInfo: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 10,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  alarmLegend: {
    marginTop: 10,
    color: "#7f1d1d",
    fontSize: 12,
    lineHeight: 1.45,
  },

  // Botón VOLVER
  btnBack: {
    background: "#0ea5e9",
    color: "#fff",
    border: 0,
    borderRadius: 10,
    padding: "10px 14px",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(14,165,233,.25)",
  },
btnOfficial: {
    background: "#b45309",
    color: "#fff",
    borderRadius: 10,
    border: 0,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 800,
  },

  btnGhost: {
    background: "#fff",
    color: "#0f172a",
    borderRadius: 10,
    border: `1px solid ${brd}`,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 700,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
  },

  modalCard: {
    width: "min(620px, 96vw)",
    background: "#fff",
    borderRadius: 18,
    border: `1px solid ${brd}`,
    boxShadow: "0 24px 60px rgba(2,6,23,.25)",
    overflow: "hidden",
  },

  modalHeader: {
    padding: "18px 20px 8px",
  },

  modalTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 900,
    color: ink,
  },

  modalSubtitle: {
    margin: "6px 0 0",
    color: "#475569",
    fontSize: 14,
  },

  modalBody: {
    display: "grid",
    gap: 14,
    padding: "12px 20px 18px",
  },

  textarea: {
    minHeight: 100,
    padding: "12px",
    borderRadius: 12,
    border: `1px solid ${brd}`,
    background: panelSoft,
    color: ink,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    padding: "0 20px 20px",
    flexWrap: "wrap",
  },
};



